import * as React from 'react';
import classes from './Button.module.scss';
import { Fragment, MouseEventHandler } from 'react';
import classNames from 'classnames';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { BreakpointVariationsType } from '../../../models/breakpoints';
import { IconsEnum } from '../../../constants/icons';
import { WithChildren } from '../../../models/children-props';
import { Icon } from '../../images/Icon/Icon';

export type IconType = {
  side: 'left' | 'right' | 'center';
  value: IconsEnum;
};

export type ButtonViewProps = WithChildren<{
  type: 'button' | 'a' | 'submit' | 'reset';
  action?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  active?: boolean;
  variant: 'solid' | 'clear' | 'hollow';
  size: 'large' | 'medium' | 'small' | 'xSmall';
  color: ButtonColorsEnum;
  expanded?: BreakpointVariationsType | true;
  wrapper?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: IconType;
  rounded?: boolean;
  justifiedIcon?: boolean;
  className?: string;
}>;

export const ButtonView = (props: ButtonViewProps): JSX.Element => {
  const ButtonTag = `${props.type === 'a' ? 'a' : 'button'}` as keyof Pick<JSX.IntrinsicElements, 'a' | 'button'>;
  const ButtonTagType = ButtonTag === 'a' ? undefined : (props.type as 'button' | 'submit' | 'reset');
  const disabled = !!props.disabled;
  let resolvedType;

  switch (props.variant) {
    case 'clear':
      resolvedType = props.active
        ? classes[`${props.color}ButtonColorClearActive`]
        : classes[`${props.color}ButtonColorClear`];
      break;
    case 'hollow':
      resolvedType = props.active
        ? classes[`${props.color}${props.size}ButtonColorHollowActive`]
        : classes[`${props.color}${props.size}ButtonColorHollow`];
      break;
    case 'solid':
    default:
      resolvedType = props.active
        ? classes[`${props.color}ButtonColorSolidActive`]
        : classes[`${props.color}ButtonColorSolid`];
      break;
  }

  const expandedResponsiveClass =
    props.expanded && props.expanded !== true
      ? classes[`${props.expanded.breakpoint}-${props.expanded.variant}-expanded`]
      : null;

  const expandedClass = classNames(expandedResponsiveClass, {
    [classes.Expanded]: props.expanded === true
  });

  const iconColor = `${props.variant === 'solid' ? 'white' : props.color}`;
  const fullIconClassName = `icon-${props.size}-${iconColor}-${props.icon?.side}`;
  const iconClasses = classNames(classes[fullIconClassName]);
  const expandedJustifiedSide =
    props.justifiedIcon && props.icon?.side === 'left' ? classes.LeftJustifiedIcon : classes.RightJustifiedIcon;

  const viewClasses = classNames(
    classes.Button,
    classes[`${props.size}Button`],
    resolvedType,
    expandedClass,
    iconClasses,
    'relative',
    {
      [classes.Rounded]: !!props.rounded,
      [classes.Hollow]: props.variant === 'hollow',
      [classes.Clear]: props.variant === 'clear',
      [classes.Loading]: !!props.loading,
      [classes.Active]: !!props.active
    },
    props.className
  );

  const iconTextClass =
    props.icon?.side === 'center' ? classes.IconTextCenter : classes[`${fullIconClassName}IconOffset`];
  const iconWrapperClasses = classNames(classes.IconWrapper, { [classes.HideText]: props.loading });
  const loaderClass = classNames(classes[`loader-${props.variant}-${props.color}`]);
  const justifiedIconWrapperClasses = classNames('grid-x', 'align-justify', { [classes.HideText]: props.loading });

  // Buffer and output view
  let iconContent = <div className={classNames({ [classes.HideText]: props.loading })}>{props.children}</div>;
  if (props.icon) {
    if (props.justifiedIcon) {
      const text = (
        <div className={'cell shrink'}>
          <div className={iconTextClass}>
            {props.children || <span dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />}
          </div>
        </div>
      );
      const icon = (
        <div className={'cell shrink'}>
          <i className={expandedJustifiedSide}>
            <Icon name={props.icon.value} size={'medium'} />
          </i>
        </div>
      );
      iconContent = (
        <div className={justifiedIconWrapperClasses}>
          {props.icon.side === 'left' ? (
            <Fragment>
              {icon}
              {text}
            </Fragment>
          ) : (
            <Fragment>
              {text}
              {icon}
            </Fragment>
          )}
        </div>
      );
    } else {
      iconContent = (
        <div className={iconWrapperClasses}>
          <i>
            <Icon name={props.icon.value} size={'medium'} />
          </i>
          <div className={iconTextClass}>
            {props.children || <span dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />}
          </div>
        </div>
      );
    }
  }

  const loaderContent = props.loading ? (
    <div className={classes.LoaderWrapper}>
      <div className={loaderClass}>Loading...</div>
    </div>
  ) : null;

  let buffer = (
    <ButtonTag
      type={ButtonTagType}
      {...(props.action ? { onClick: props.action } : {})}
      className={viewClasses}
      disabled={disabled}>
      {loaderContent}
      {iconContent}
    </ButtonTag>
  );

  if (props.wrapper) {
    buffer = <div className={'input-wrapper'}>{buffer}</div>;
  }
  return <Fragment>{buffer}</Fragment>;
};
